import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BehaviorSubject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, take } from 'rxjs/operators';

import { AUTH_RETURN_URL_STORAGE_KEY } from './auth.config';
import { ConnectUserProfile } from '../models/connect-user.model';
import { ConnectUserService } from '../services/connect-user.service';
import { ToastService } from '../services/toast.service';

const ACCESS_RESTRICTED_TOAST_STORAGE_KEY = 'amplify_access_restricted_toast';
const ACCESS_RESTRICTED_TOAST_MESSAGE = 'Access is restricted to Allplan Employees';

interface AuthIdentityState {
  fallbackDisplayName: string;
  displayName: string | null;
  organization: string | null;
  avatarUrl: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly connectUserService = inject(ConnectUserService);
  private readonly toastService = inject(ToastService);
  private readonly subscriptions = new Subscription();
  private avatarObjectUrl: string | null = null;
  private readonly identityStateSubject = new BehaviorSubject<AuthIdentityState>({
    fallbackDisplayName: 'User',
    displayName: null,
    organization: null,
    avatarUrl: null
  });

  readonly isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map(({ isAuthenticated }) => isAuthenticated)
  );

  readonly userName$ = this.oidcSecurityService.userData$.pipe(
    map(({ userData }) => userData?.name || userData?.preferred_username || userData?.email || 'User')
  );

  readonly displayName$ = this.identityStateSubject.pipe(map((state) => state.displayName));
  readonly hasUser$ = this.identityStateSubject.pipe(map((state) => !!state.displayName));
  readonly organization$ = this.identityStateSubject.pipe(map((state) => state.organization));
  readonly avatarUrl$ = this.identityStateSubject.pipe(map((state) => state.avatarUrl));

  getCurrentDisplayName(): string {
    const state = this.identityStateSubject.value;
    return state.displayName ?? state.fallbackDisplayName;
  }

  constructor() {
    this.showPendingAccessRestrictedToast();

    this.subscriptions.add(
      this.userName$.subscribe((userName = 'User') => {
        const nextFallbackDisplayName = userName;

        this.identityStateSubject.next({
          ...this.identityStateSubject.value,
          fallbackDisplayName: nextFallbackDisplayName,
          displayName: this.identityStateSubject.value.displayName
        });
      })
    );

    this.subscriptions.add(
      this.isAuthenticated$.pipe(distinctUntilChanged()).subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
          this.resetIdentityState();
          return;
        }

        this.loadConnectIdentity();
      })
    );
  }

  login(): void {
    this.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        void this.router.navigateByUrl(this.consumeReturnUrl() || '/define');
        return;
      }

      this.oidcSecurityService.authorize();
    });
  }

  logout(): void {
    this.clearReturnUrl();
    this.resetIdentityState();
    this.oidcSecurityService.logoff().subscribe();
  }

  rememberReturnUrl(url: string): void {
    if (!url || url.startsWith('/landing') || url.startsWith('/callback')) {
      return;
    }

    sessionStorage.setItem(AUTH_RETURN_URL_STORAGE_KEY, url);
  }

  consumeReturnUrl(): string | null {
    const url = sessionStorage.getItem(AUTH_RETURN_URL_STORAGE_KEY);

    if (url) {
      sessionStorage.removeItem(AUTH_RETURN_URL_STORAGE_KEY);
    }

    return url;
  }

  clearReturnUrl(): void {
    sessionStorage.removeItem(AUTH_RETURN_URL_STORAGE_KEY);
  }

  private loadConnectIdentity(): void {
    this.subscriptions.add(
      forkJoin({
        profile: this.connectUserService.getCurrentUser().pipe(
          catchError(() => of(null as ConnectUserProfile | null))
        ),
        picture: this.connectUserService.getCurrentUserPicture().pipe(
          catchError(() => of(null as Blob | null))
        )
      }).subscribe(({ profile, picture }) => {
        this.revokeAvatarObjectUrl();

        const nextAvatarUrl = picture ? URL.createObjectURL(picture) : null;
        this.avatarObjectUrl = nextAvatarUrl;

        if (!this.isAllplanCompany(profile)) {
          this.handleRestrictedAccess();
          return;
        }

        this.identityStateSubject.next({
          fallbackDisplayName: this.identityStateSubject.value.fallbackDisplayName,
          displayName: profile?.displayName || null,
          organization: profile?.organization || null,
          avatarUrl: nextAvatarUrl
        });
      })
    );
  }

  private isAllplanCompany(profile: ConnectUserProfile | null): boolean {
    return profile?.sfUserGroup?.trim() === 'Allplan';
  }

  private handleRestrictedAccess(): void {
    sessionStorage.setItem(ACCESS_RESTRICTED_TOAST_STORAGE_KEY, ACCESS_RESTRICTED_TOAST_MESSAGE);
    this.toastService.error(ACCESS_RESTRICTED_TOAST_MESSAGE, 5000);

    this.clearReturnUrl();
    this.resetIdentityState();
    void this.router.navigateByUrl('/landing');

    this.oidcSecurityService.logoff().subscribe({
      error: () => {
        void this.router.navigateByUrl('/landing');
      }
    });
  }

  private showPendingAccessRestrictedToast(): void {
    const pendingMessage = sessionStorage.getItem(ACCESS_RESTRICTED_TOAST_STORAGE_KEY);

    if (!pendingMessage) {
      return;
    }

    sessionStorage.removeItem(ACCESS_RESTRICTED_TOAST_STORAGE_KEY);
    this.toastService.error(pendingMessage, 5000);
  }

  private resetIdentityState(): void {
    this.revokeAvatarObjectUrl();
    this.identityStateSubject.next({
      fallbackDisplayName: 'User',
      displayName: null,
      organization: null,
      avatarUrl: null
    });
  }

  private revokeAvatarObjectUrl(): void {
    if (this.avatarObjectUrl) {
      URL.revokeObjectURL(this.avatarObjectUrl);
      this.avatarObjectUrl = null;
    }
  }
}
